/**
 * Tamamlıyo sigorta dikeyi — legacy MVC panelinde InsuranceController YOKTU, tamamen yeni
 * yüzey. Rotalar `Docs/api/03-hotel-car-transfer-bus-insurance.md` § Insurance'tan, canlı
 * doğrulanmadı. `Insurance/GetPriceB2C` backend'de `NotImplementedException` — wrapper
 * tamlık için duruyor, UI yok.
 *
 * Session enjeksiyonu: `Insurance/GetCountries` GET + session'sız (`apiGet`); geri kalan
 * her rota `apiPost` (varsayılan `{ ...body, sessionHeader }`).
 */
import { apiGet, apiPost } from '../client';
import { LONG_TIMEOUT_MS } from '../../config/env';
import type {
  InsuranceCancelRequestInput,
  InsuranceCancelResponse,
  InsuranceCountriesResponse,
  InsuranceCreateOfferRequest,
  InsuranceCreatePolicyRequest,
  InsuranceCreatePolicyResponse,
  InsuranceDistrictsResponse,
  InsuranceGetPriceRequest,
  InsuranceGetPriceResponse,
  InsuranceOfferInfoResponse,
  InsuranceOfferResponse,
  InsurancePaymentRequestInput,
  InsurancePaymentResponse,
  InsurancePdfResponse,
  InsuranceProductsResponse,
  InsuranceProvincesResponse,
} from '../types/insurance';
import type { PaymentRequest } from '../types/payment';

/** `Insurance/ProductCodes` — sağlayıcının sigorta ürün kodları. Gövde `{ sessionHeader }`. */
export function getInsuranceProducts(): Promise<InsuranceProductsResponse> {
  return apiPost<Record<string, never>, InsuranceProductsResponse>('Insurance/ProductCodes', {});
}

/** `Insurance/GetPrice` — N sigortalı + tarih aralığı için teklif fiyatı. */
export function getInsurancePrice(
  req: InsuranceGetPriceRequest
): Promise<InsuranceGetPriceResponse> {
  return apiPost<InsuranceGetPriceRequest, InsuranceGetPriceResponse>('Insurance/GetPrice', req, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

/**
 * `Insurance/GetPriceB2C` — **backend `throw new NotImplementedException()`**. Wrapper
 * yalnız tamlık için tutuluyor; şu an hiçbir ekran çağırmıyor (eski pasif "B2C Fiyatı"
 * kartı 2026-09-08'de kaldırıldı).
 */
export function getInsurancePriceB2C(
  req: InsuranceGetPriceRequest
): Promise<InsuranceGetPriceResponse> {
  return apiPost<InsuranceGetPriceRequest, InsuranceGetPriceResponse>('Insurance/GetPriceB2C', req);
}

/** `Insurance/CreateOffer` (worker `MakeOffer`) — `teklifId` üretir. */
export function createInsuranceOffer(
  req: InsuranceCreateOfferRequest
): Promise<InsuranceOfferResponse> {
  return apiPost<InsuranceCreateOfferRequest, InsuranceOfferResponse>('Insurance/CreateOffer', req, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

/** `Insurance/GetOfferInfo` — `teklifId` ile teklifin güncel durumu. */
export function getInsuranceOfferInfo(teklifId: string): Promise<InsuranceOfferInfoResponse> {
  return apiPost<{ teklifId: string }, InsuranceOfferInfoResponse>('Insurance/GetOfferInfo', {
    teklifId,
  });
}

/**
 * `Insurance/CreatePolicy` — onaylı teklif + `ShoppingCart`'tan poliçe keser.
 * NOT: CreateOffer bir sepet kimliği DÖNDÜRMÜYOR (kod-izli DTO'da yok); sepet ödeme
 * adımından iliştiriliyor — canlı doğrulanmalı (bkz. `modules/insurance/flowConfig.ts`).
 */
export function createInsurancePolicy(
  req: InsuranceCreatePolicyRequest
): Promise<InsuranceCreatePolicyResponse> {
  return apiPost<InsuranceCreatePolicyRequest, InsuranceCreatePolicyResponse>(
    'Insurance/CreatePolicy',
    req,
    { timeoutMs: LONG_TIMEOUT_MS }
  );
}

/** `Insurance/GetCountries` — **session'sız GET**, `hasError` zarfı beklenmez. */
export function getInsuranceCountries(): Promise<InsuranceCountriesResponse> {
  return apiGet<InsuranceCountriesResponse>('Insurance/GetCountries');
}

/** `Insurance/GetProvinces` — ülke için iller. */
export function getInsuranceProvinces(ulkeId: number): Promise<InsuranceProvincesResponse> {
  return apiPost<{ ulkeId: number }, InsuranceProvincesResponse>('Insurance/GetProvinces', {
    ulkeId,
  });
}

/** `Insurance/GetDistricts` — il için ilçeler. */
export function getInsuranceDistricts(ilId: number): Promise<InsuranceDistrictsResponse> {
  return apiPost<{ ilId: number }, InsuranceDistrictsResponse>('Insurance/GetDistricts', { ilId });
}

/** `Insurance/PolicyPDF` — poliçe PDF'i. `rawPdfContent` base64 string döner (blob değil). */
export function getInsurancePolicyPdf(teklifId: string): Promise<InsurancePdfResponse> {
  return apiPost<{ teklifId: string }, InsurancePdfResponse>('Insurance/PolicyPDF', { teklifId }, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

/** `Insurance/ReceiptPDF` — makbuz PDF'i (poliçe ile aynı şekil). */
export function getInsuranceReceiptPdf(teklifId: string): Promise<InsurancePdfResponse> {
  return apiPost<{ teklifId: string }, InsurancePdfResponse>('Insurance/ReceiptPDF', { teklifId }, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

/** `Insurance/CancelRequest` (worker `CreateCancelRequest`) — poliçe iptal talebi. */
export function createInsuranceCancelRequest(
  req: InsuranceCancelRequestInput
): Promise<InsuranceCancelResponse> {
  return apiPost<InsuranceCancelRequestInput, InsuranceCancelResponse>(
    'Insurance/CancelRequest',
    req
  );
}

/**
 * `MakePayment` (paylaşılan rota) — sigorta sepeti için ödeme. `paymentChannel: 0`
 * (normal akış). Kart 3DS banka URL'i dönerse çağıran `redirectToBankIfNeeded` ile
 * yönlendirir.
 */
export function makeInsurancePayment(
  input: InsurancePaymentRequestInput
): Promise<InsurancePaymentResponse> {
  const body: PaymentRequest = {
    shoppingCartId: input.shoppingCartId,
    paymentForm: input.paymentForm,
    amount: input.amount,
    paymentType: input.paymentForm ? 'ThreeDPayment' : 'CurrentPayment',
    paymentChannel: 0,
  };
  return apiPost<PaymentRequest, InsurancePaymentResponse>('MakePayment', body, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

/**
 * `GetPdfResponse.rawPdfContent` base64 → yeni sekmede aç. `Insurance/{PolicyPDF,ReceiptPDF}`
 * düz JSON zarfı döndürdüğü için `apiPostBlob` kullanılmıyor — burada elle çözülüyor.
 */
export function openInsurancePdf(base64: string, filename: string): void {
  const clean = base64.replace(/\s/g, '');
  const byteChars = atob(clean);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i += 1) bytes[i] = byteChars.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  if (!win) {
    // Açılır pencere engellendiyse indirmeye düş.
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
